import {
  getDcfExperience,
  getIsFeatureToggleEnabled,
  getSessionId,
  getVsbFlow
} from 'communicator/store/selectors';
import {
  GetIntentResponse,
  PurchaseIntentResponse,
  PutIntentRequestPayload,
  SetIntentRequestPayload,
  SetIntentResponse
} from 'types/api/intent';
import { FetchResponse, ParsedErrorResponse } from 'types/fetch';
import endpoints from '../endpoints';
import fetchReq from '../fetch';
import getVbaToken from '../get-vba-token';
import { markEnd, markStart } from '../instrumentation';

type FetchPostIntentOptions = {
  accessToken: string;
  apikey: string;
  intentPayload: SetIntentRequestPayload;
};

export async function fetchPostIntent({
  accessToken,
  apikey,
  intentPayload
}: FetchPostIntentOptions): FetchResponse<SetIntentResponse, ParsedErrorResponse> {
  const dcfExperience = getDcfExperience();
  let url = `${endpoints.intent}?apikey=${apikey}`;

  // Ignoring coverage here because integrated flows should always call /cardList
  // istanbul ignore if
  if (dcfExperience) {
    url += `&dcfExperience=${dcfExperience}`;
  }

  markStart('gsp_post_intent_LEGACY');
  const res = await fetchReq({
    body: intentPayload,
    headers: {
      access_token: accessToken
    },
    mark: 'post_intent',
    method: 'POST',
    url
  }).then(res => {
    markEnd('gsp_post_intent_LEGACY');
    return res;
  });

  return res;
}

type FetchGetIntentOptions = {
  accessToken?: string;
  apikey: string;
  vIntentId: string;
};

export function fetchGetIntent({
  accessToken,
  apikey,
  vIntentId
}: FetchGetIntentOptions): FetchResponse<GetIntentResponse> {
  return fetchReq({
    headers: {
      access_token: accessToken
    },
    mark: 'get_intent',
    method: 'GET',
    url: `${endpoints.intent}/${vIntentId}?apikey=${apikey}&visaCardsOnly=true`
  });
}

export type FetchPutIntentOptions = {
  accessToken: string;
  apikey: string;
  requestPayload: PutIntentRequestPayload;
  sessionQualifier?: string;
  // START GENAI@CHATGPT4
  signal?: AbortSignal;
  // END GENAI@CHATGPT4
  vIntentId: string;
  vsbFlow?: boolean;
};

export function fetchPutIntent({
  accessToken,
  apikey,
  requestPayload,
  vIntentId,
  sessionQualifier,
  signal,
  vsbFlow = false
}: FetchPutIntentOptions): FetchResponse<PurchaseIntentResponse> {
  vsbFlow = getVsbFlow();

  //START GENAI@GHCOPILOT
  const isOrchestratorEnabled = getIsFeatureToggleEnabled('orchestratorCheckoutApi');

  let url = isOrchestratorEnabled
    ? endpoints.orchestrator.checkout
    : `${endpoints.purchaseIntent}/${vIntentId}?apikey=${apikey}`;

  if (vsbFlow) {
    url += (url.includes('?') ? '&' : '?') + 'vsbFlow=true'; //eslint-disable-line
  }

  let putIntentHeaders: Record<string, string> = { access_token: accessToken };

  if (isOrchestratorEnabled) {
    const sessionId = getSessionId();
    const vbaTokenPromise = Promise.resolve(getVbaToken());

    return vbaTokenPromise.then(vbaToken => {
      putIntentHeaders = {
        ...putIntentHeaders,
        dfpSessionId: sessionId,
        'V-C-VAAP-TOKEN': vbaToken || '',
        'X-THMID': sessionId
      };

      if (sessionQualifier) {
        putIntentHeaders['X-IAM-SESSION-QUALIFIER'] = sessionQualifier;
      }

      return fetchReq({
        body: requestPayload,
        headers: putIntentHeaders,
        mark: 'put_intent',
        method: 'POST',
        signal,
        url
      });
    });
  }

  if (sessionQualifier) {
    putIntentHeaders['X-IAM-SESSION-QUALIFIER'] = sessionQualifier;
  }

  return fetchReq({
    body: requestPayload,
    headers: putIntentHeaders,
    mark: 'put_intent',
    method: 'PUT',
    signal,
    url
  });
  //END GENAI@GHCOPILOT
}
